import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Grid,
  Button,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import CallReceivedRoundedIcon from '@mui/icons-material/CallReceivedRounded';
import PhoneMissedRoundedIcon from '@mui/icons-material/PhoneMissedRounded';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

/** Purpose: Perform format duration for the calls module. Why: Callers need a consistent result from this shared operation. */
const formatDuration = (seconds) => {
  const total = Number(seconds || 0);
  if (!total) return '0s';
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return secs ? `${mins}m ${secs}s` : `${mins}m`;
};

/** Purpose: list voice-call activity and outcomes. Why: support teams need a searchable record of voice conversations alongside chat channels. */
export default function Calls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { selectedBot, selectedEnv } = useAuth();

  useEffect(() => {
    fetchCalls();
  }, [selectedBot, selectedEnv]);

  /** Purpose: Load fetch calls for the calls module. Why: The selected view needs current data to render or continue processing. */
  const fetchCalls = async () => {
    if (!selectedBot) {
      setCalls([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get('/api/v1/calls', {
        params: {
          botId: selectedBot,
          env: selectedEnv || 'dev',
        },
      });
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Perform get status color for the calls module. Why: Callers need a consistent result from this shared operation. */
  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      'in-progress': 'info',
      failed: 'error',
      'no-answer': 'warning',
    };
    return colors[status] || 'default';
  };

  /** Purpose: Perform get direction icon for the calls module. Why: Callers need a consistent result from this shared operation. */
  const getDirectionIcon = (direction) => {
    if (direction === 'incoming') return <CallReceivedRoundedIcon fontSize="small" />;
    if (direction === 'outgoing') return <CallMadeRoundedIcon fontSize="small" />;
    return <CallRoundedIcon fontSize="small" />;
  };

  /** Purpose: Resolve completed calls for the calls module. Why: The surrounding operation uses this value to make its decision. */
  const completedCalls = calls.filter((call) => call.status === 'completed');
  const averageDuration = completedCalls.length
    ? Math.round(
        completedCalls.reduce((sum, call) => sum + Number(call.duration || 0), 0) / completedCalls.length
      )
    : 0;
  const missedCalls = calls.filter((call) => call.status === 'no-answer').length;

  const stats = [
    {
      label: 'Total calls',
      value: calls.length,
      helper: 'All voice interactions',
      accent: 'rgba(59,130,246,0.08)',
      accentColor: '#2563eb',
    },
    {
      label: 'Completed',
      value: completedCalls.length,
      helper: 'Finished successfully',
      accent: 'rgba(16,185,129,0.08)',
      accentColor: '#059669',
    },
    {
      label: 'Average duration',
      value: formatDuration(averageDuration),
      helper: 'Across completed calls',
      accent: 'rgba(245,158,11,0.09)',
      accentColor: '#d97706',
    },
    {
      label: 'Missed / failed',
      value: missedCalls + calls.filter((call) => call.status === 'failed').length,
      helper: 'Need follow-up',
      accent: 'rgba(244,63,94,0.08)',
      accentColor: '#e11d48',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: '100%' }}>
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(15,118,110,0.09) 58%, rgba(15,23,42,0.96))'
            : 'linear-gradient(135deg, rgba(37,99,235,0.055), rgba(15,118,110,0.025) 58%, rgba(255,255,255,0.92))',
          boxShadow: '0 8px 28px rgba(15,23,42,0.045)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={2} flexWrap="wrap">
          <Box>
            <Typography sx={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: 'text.secondary', mb: 0.5, fontWeight: 700 }}>
              Voice activity
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.04em', mb: 0.5 }}>
              Calls
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
              Review call outcomes, spot failed conversations quickly, and jump into details with less friction.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={fetchCalls}
              disabled={!selectedBot}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {stats.map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Paper
                sx={{
                  p: 1.75,
                  borderRadius: 2.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: stat.accent,
                  borderLeft: `3px solid ${stat.accentColor}`,
                  minHeight: 108,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: '0.075em', textTransform: 'uppercase', fontWeight: 700 }}>
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {stat.helper}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', flex: 1 }}>
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 750, letterSpacing: '-0.02em' }}>
              Recent calls
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Call history, outcomes, and quick access to details.
            </Typography>
          </Box>
          <Chip label={`${calls.length} ${calls.length === 1 ? 'call' : 'calls'}`} size="small" variant="outlined" sx={{ fontWeight: 650 }} />
        </Box>
        <Divider />

        {calls.length === 0 ? (
          <Box
            sx={{
              px: 3,
              py: 5,
              minHeight: 280,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(56,189,248,0.12)', color: 'primary.main' }}>
              <PhoneMissedRoundedIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              No calls yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
              Call activity will appear here once users interact with your voice bot or connected telephony flows.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.5 }}>
              <Button variant="contained" size="small" onClick={() => navigate('/channels')}>
                Connect a channel
              </Button>
              <Button variant="outlined" size="small" onClick={() => navigate('/flows')}>
                Review journeys
              </Button>
            </Stack>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Caller</TableCell>
                  <TableCell>Call SID</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((call) => (
                  <TableRow
                    key={call._id}
                    hover
                    sx={{
                      '& td': { borderColor: 'divider' },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(56,189,248,0.14)', color: 'primary.main' }}>
                          <CallRoundedIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {call.callerNumber || 'Unknown caller'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Voice interaction
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {call.callSid}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getDirectionIcon(call.direction)}
                        label={call.direction || 'unknown'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={call.status}
                        color={getStatusColor(call.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDuration(call.duration)}</TableCell>
                    <TableCell>
                      {call.createdAt ? format(new Date(call.createdAt), 'PPp') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/calls/${call._id}`)}
                        title="View call"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
